import { taskOrIssueENUM } from "@mctypes/global.types";
import { NumberFormatter } from "@mantine/core";
import { ICapitalLetter, IOtherAttributeConfigItemProps } from "@schemas/otherAttributesSchema";
import { ColDef } from "ag-grid-community";
import { CustomCellEditorProps, CustomCellRendererProps } from "ag-grid-react";
import React, { forwardRef, Ref, useImperativeHandle, useMemo, useRef, useState } from "react";
import { HexColorInput, HexColorPicker } from "react-colorful";
import { useHistory } from "react-router-dom";
import Switch from "react-switch";
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Popover,
  UncontrolledDropdown,
} from "reactstrap";
import { useImmer } from "use-immer";
import { useOnClickOutside } from "usehooks-ts";
import { STANDARD_COLORS } from "../../../app/globalConstants";
import { useGetSingleIssueBoardQuery } from "../../../app/routes/issuesApp/IssueBoardRTK";
import { selectIsMathFormulaConfigValid } from "../../../app/routes/issuesApp/issueSelectors";
import { getIssueBoardIdFromUrl } from "../../../app/routes/issuesApp/issueUtils";
import { getProjectIdFromUrl } from "../../../app/utils";
import { useAppSelector } from "../../../hooks";
import { ConfirmAbortModal } from "../../ConfirmAbortModal/ConfirmAbortModal";
import { McDropdown, McDropdownBase, McInput, McTitle, VSpace } from "../../index";
import McTypeahead from "../../McTypeahead/McTypeahead";
import ViewEditTable, { ViewEditTableRef } from "../../ViewEditTable/ViewEditTable";
import { VETEmptyAndNoDuplicatesValidator } from "../../ViewEditTable/ViewEditTableValidators";
import FormulaConfig from "./FormulaConfig";
import MathFormulaConfig from "./MathFormulaConfig/MathFormulaConfig";
import { IOtherAttributeConfigItemPropsWithId, TYPE_OF_VALUE_OPTIONS } from "./OtherAttributesConfig";

const OtherAttributeItemConfigModal: React.FC<{
  onClose: () => void;
  onSave: (otherAttributesConfigItemProps: IOtherAttributeConfigItemProps) => void;
  title: string;
  orignalOtherAttributesConfigItemProps?: IOtherAttributeConfigItemPropsWithId;
  addOrEdit: "add" | "edit";
  existingSections: string[];
  taskOrIssue?: taskOrIssueENUM;
}> = ({
  onClose,
  onSave,
  title,
  orignalOtherAttributesConfigItemProps = {} as IOtherAttributeConfigItemPropsWithId,
  addOrEdit,
  existingSections,
  taskOrIssue,
}) => {
  const template: IOtherAttributeConfigItemProps = {
    typeOfValue: "STRING",
    label: "",
    labelColor: "",
    dropdownOptionType: "STRING",
    tableOptions: [],
    allowMultipleSelectionsInDropdown: false,
    description: "",
    section: "",
    order: 0,
    isActive: true,
    formula: {
      nrOfCharsInCounter: 3,
      confArr: [],
    },
    mathFormula: {
      expression: "",
      variables: [],
    },
  };

  const [editedOtherAttributesConfigItemProps, setConfigItem] = useImmer<IOtherAttributeConfigItemProps>(
    addOrEdit === "add" ? template : orignalOtherAttributesConfigItemProps,
  );

  const updateAttribute = (key: keyof IOtherAttributeConfigItemProps, value: any) => {
    setConfigItem((draft) => {
      (draft[key] as any) = value;
    });
  };

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const isMathFormulaConfigValid = useAppSelector((state) =>
    selectIsMathFormulaConfigValid(state, orignalOtherAttributesConfigItemProps?.id),
  );

  const isDisabled =
    editedOtherAttributesConfigItemProps.label === "" ||
    editedOtherAttributesConfigItemProps.section === "" ||
    (editedOtherAttributesConfigItemProps.typeOfValue === "MATH_FORMULA" && isMathFormulaConfigValid === false);

  const _existingSectionOptions = existingSections.map((section, index) => {
    return {
      id: index,
      displayValue: section,
    };
  });

  const getDefaultSelected = () => {
    const existing = _existingSectionOptions.find(
      (section) => section.displayValue === editedOtherAttributesConfigItemProps.section,
    );
    if (existing != null) {
      return [existing];
    } else {
      return [];
    }
  };
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);

  const toolTipRef = useRef(null);

  const handleClickOutside = () => {
    setTooltipVisible(false);
  };

  useOnClickOutside(toolTipRef, handleClickOutside);

  const tableOptionsRef = useRef<TableOptionsRef>(null);

  const _closeInternal = () => {
    const tableData = tableOptionsRef.current?.getData();
    const current = {
      ...editedOtherAttributesConfigItemProps,
      tableOptions: tableData?.rows ?? orignalOtherAttributesConfigItemProps?.tableOptions ?? [],
    };

    const changed = JSON.stringify(orignalOtherAttributesConfigItemProps) !== JSON.stringify(current);

    // if (changed === true) {
    //   setConfirmModalOpen(true); // firing all the time
    // } else {
    //   onClose();
    // }

    onClose();
  };

  const _saveInternal = () => {
    const tableData = tableOptionsRef.current?.getData();
    let newEditedOtherAttributesConfigItemProps = editedOtherAttributesConfigItemProps;

    // type is DROPDOWN
    if (tableData != null) {
      const { rows, validationErrors } = tableData;
      const normalizedDropdownRows = normalizeDropdownOptionRows({
        rows,
        dropdownOptionType: editedOtherAttributesConfigItemProps.dropdownOptionType,
      });
      const allValidationErrors = [...validationErrors, ...normalizedDropdownRows.validationErrors];

      if (allValidationErrors.length > 0) {
        setValidationErrors(allValidationErrors);
        setValidationModalOpen(true);
        return;
      }

      setConfigItem((draft) => {
        draft.tableOptions = normalizedDropdownRows.normalizedRows;
      });

      newEditedOtherAttributesConfigItemProps = {
        ...editedOtherAttributesConfigItemProps,
        dropdownOptionType: editedOtherAttributesConfigItemProps.dropdownOptionType ?? "STRING",
        tableOptions: normalizedDropdownRows.normalizedRows,
      };
    }

    const hasChanged =
      JSON.stringify(orignalOtherAttributesConfigItemProps) !== JSON.stringify(newEditedOtherAttributesConfigItemProps);

    if (hasChanged === true) {
      onSave(newEditedOtherAttributesConfigItemProps);
      return;
    }

    onClose();
  };

  return (
    <>
      <Modal isOpen style={{ maxWidth: "850px", width: "80%" }}>
        <ModalHeader toggle={_closeInternal}>{title}</ModalHeader>
        <ModalBody id="dropdownContainer">
          <McTitle title="Tittel">
            <McInput
              value={editedOtherAttributesConfigItemProps.label}
              onChange={(e) => updateAttribute("label", e.target.value)}
            />
          </McTitle>
          <McTitle title="Beskrivelse">
            <McInput
              value={editedOtherAttributesConfigItemProps.description}
              onChange={(e) => updateAttribute("description", e.target.value)}
              type="textarea"
            />
          </McTitle>
          <McTitle title="Seksjon">
            <McTypeahead
              defaultSelected={getDefaultSelected()}
              allowNew
              id="1"
              onBlur={(e) => {
                const target = e.target as HTMLInputElement;
                updateAttribute("section", target.value ?? "");
              }}
              placeholder="Angi seksjon"
              labelKey="displayValue"
              options={_existingSectionOptions}
            />
          </McTitle>
          <McTitle title="Type verdi">
            <McDropdown
              disabled={addOrEdit === "edit"}
              currentValue={TYPE_OF_VALUE_OPTIONS.find(
                (o) => o.value === editedOtherAttributesConfigItemProps.typeOfValue,
              )}
              onChange={(_, valueAndLabel) =>
                updateAttribute(
                  "typeOfValue",
                  TYPE_OF_VALUE_OPTIONS.find((o) => o.value === valueAndLabel.value)?.value,
                )
              }
              options={
                taskOrIssue === "TASK"
                  ? TYPE_OF_VALUE_OPTIONS.filter((opt) => opt.value !== "FORMULA" && opt.value !== "MATH_FORMULA")
                  : TYPE_OF_VALUE_OPTIONS
              }
              displayKey="label"
            />
          </McTitle>
          {editedOtherAttributesConfigItemProps.typeOfValue === "DROPDOWN" && (
            <TableOptions
              addOrEdit={addOrEdit}
              editedOtherAttributesConfigItemProps={editedOtherAttributesConfigItemProps}
              updateAttribute={updateAttribute}
              ref={tableOptionsRef}
            />
          )}
          {editedOtherAttributesConfigItemProps.typeOfValue === "FORMULA" && (
            <FormulaConfig
              formula={
                editedOtherAttributesConfigItemProps.formula || {
                  nrOfCharsInCounter: 3,
                  confArr: [],
                }
              }
              onFormulaConfUpdate={(formula) => updateAttribute("formula", formula)}
            />
          )}
          {editedOtherAttributesConfigItemProps.typeOfValue === "MATH_FORMULA" && (
            <>
              <div
                className="alert alert-warning d-flex align-items-start"
                style={{ gap: "10px", marginTop: "12px", marginBottom: "0px" }}
              >
                <i className="fa fa-flask fa-fw" style={{ marginTop: "3px" }} />
                <div>
                  <b>BETA</b>
                  <div style={{ marginTop: "4px" }}>
                    Matematiske formler er fortsatt under innføring. Enkelte begrensninger og hvordan formler vises i
                    appen kan bli justert.
                  </div>
                </div>
              </div>
              <MathFormulaConfig
                initialMathFormula={
                  editedOtherAttributesConfigItemProps.mathFormula || {
                    expression: "",
                    variables: [],
                  }
                }
                currentAttributeId={orignalOtherAttributesConfigItemProps?.id}
                onMathFormulaUpdate={(mathFormula) => updateAttribute("mathFormula", mathFormula)}
              />
            </>
          )}
          <h5 className="mt-2">Farge på tittel</h5>
          <Button id="colorTooltip" className="px-3 mr-2" onClick={() => setTooltipVisible(true)}>
            <div className="d-flex align-items-center">
              <div
                className="mmi-dot mr-2"
                style={{
                  marginBottom: "0px",
                  backgroundColor: editedOtherAttributesConfigItemProps?.labelColor,
                  outline: editedOtherAttributesConfigItemProps?.labelColor ? "" : "2px solid #aeaeae",
                }}
              ></div>
              <span>velg farge</span>
            </div>
          </Button>
          <Popover
            className="colorPickerPopup"
            target="colorTooltip"
            placement="right"
            trigger="focus"
            isOpen={tooltipVisible}
          >
            <div ref={toolTipRef} style={{ padding: "10px" }}>
              <HexColorPicker
                color={editedOtherAttributesConfigItemProps?.labelColor}
                onChange={(newColor) => updateAttribute("labelColor", newColor ?? "")}
              />
              <div style={{ marginTop: "5px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <HexColorInput
                  color={editedOtherAttributesConfigItemProps?.labelColor}
                  onChange={(newColor) => updateAttribute("labelColor", newColor ?? "")}
                  style={{ width: "140px" }}
                  className="mcinput"
                  autoFocus={true}
                />
                <Button color="success" onClick={() => setTooltipVisible(false)}>
                  <i className={`fa fa-check`} />
                </Button>
              </div>
            </div>
          </Popover>
          <Button onClick={() => updateAttribute("labelColor", "")}>
            <i className={`fa fa-times`} /> Nullstill
          </Button>
          <McTitle className="mt-2" title="Låst">
            <McTitle.InlineData positionInlineData="after">
              <h5>
                <i
                  className="fa fa-info-circle fa-fw text-info"
                  title="Låste attributter kan ikke endres i tabellen"
                ></i>
              </h5>
            </McTitle.InlineData>

            <Switch
              checked={editedOtherAttributesConfigItemProps.locked}
              onChange={(bool) => updateAttribute("locked", bool)}
            />
          </McTitle>
          <McTitle className="mt-2" title="Aktiv">
            <McTitle.InlineData positionInlineData="after">
              <h5>
                <i className="fa fa-info-circle fa-fw text-info" title="Inaktive attributter vises ikke"></i>
              </h5>
            </McTitle.InlineData>

            <Switch
              checked={editedOtherAttributesConfigItemProps.isActive}
              onChange={(bool) => updateAttribute("isActive", bool)}
            />
          </McTitle>
        </ModalBody>
        <ModalFooter>
          <Button onClick={_closeInternal}>Lukk</Button>
          <Button disabled={isDisabled} color="success" onClick={() => _saveInternal()}>
            Lagre
          </Button>
        </ModalFooter>
      </Modal>
      <ConfirmAbortModal
        isOpen={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={() => {
          setConfirmModalOpen(false);
          onClose();
        }}
      />
      <Modal isOpen={validationModalOpen} toggle={() => setValidationModalOpen(validationModalOpen === false)}>
        <ModalHeader toggle={() => setValidationModalOpen(validationModalOpen === false)}>Feil i tabellen</ModalHeader>
        <ModalBody>
          <ul>
            {validationErrors.map((error, index) => (
              <li key={index}>
                <pre style={{ fontFamily: "unset" }}>{error}</pre>
              </li>
            ))}
          </ul>
          <div>Vennligst rett opp i feilene før du lagrer.</div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setValidationModalOpen(false)}>
            Ok
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default OtherAttributeItemConfigModal;

const ColorMenuEditor: React.FC<CustomCellEditorProps> = ({ value, onValueChange, stopEditing }) => {
  return (
    <UncontrolledDropdown isOpen={true} className="dropdown-menu dropdown-light" style={{ minWidth: "80px" }}>
      {Object.values(STANDARD_COLORS).map((color) => (
        <McDropdownBase.Option
          style={{ padding: ".3em .6em" }}
          key={color}
          onClick={() => {
            onValueChange(color);
            stopEditing();
          }}
        >
          <div
            style={{
              width: "100%",
              height: "20px",
              borderRadius: "5px",
              backgroundColor: color,
            }}
          />
        </McDropdownBase.Option>
      ))}
      <McDropdownBase.Option
        style={{ padding: ".3em .6em" }}
        key={"clear"}
        onClick={() => {
          onValueChange("");
          stopEditing();
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "20px",
            borderRadius: "5px",
            color: "black",
            fontSize: "14px",
          }}
        >
          <span className="mr-1" style={{ marginBottom: "3px" }}>
            NULLSTILL
          </span>
          <i className="fa fa-refresh"></i>
        </div>
      </McDropdownBase.Option>
    </UncontrolledDropdown>
  );
};

interface TableOptionsRef {
  getData: () => {
    rows: {
      value: string | number;
      description: string;
      color?: string;
    }[];
    validationErrors: string[];
  };
}

interface TableOptionsProps {
  addOrEdit: "add" | "edit";
  updateAttribute: (key: keyof IOtherAttributeConfigItemProps, value: any) => void;
  editedOtherAttributesConfigItemProps: IOtherAttributeConfigItemProps;
}

const TableOptionsRefComp = (
  { updateAttribute, editedOtherAttributesConfigItemProps, addOrEdit },
  ref: Ref<TableOptionsRef>,
) => {
  const history = useHistory();
  const location = history.location;
  const projectId = getProjectIdFromUrl(location.pathname);
  const issueBoardId = getIssueBoardIdFromUrl(location.pathname);

  const { data: issueBoard } = useGetSingleIssueBoardQuery({
    projectId: projectId,
    issueBoardId: issueBoardId,
  });

  const otherAttirbutesConfigItemPropsWithDropdown = Object.entries(issueBoard?.otherAttributesConfig || {})
    .filter(([_propKey, content]) => {
      return content.typeOfValue === "DROPDOWN";
    })
    .map(([propKey, content]) => propKey);

  const isNumberDropdown = editedOtherAttributesConfigItemProps.dropdownOptionType === "NUMBER";

  const colDefs = useMemo<ColDef<{ value: string | number; description: string; color?: string }>[]>(
    () => [
      {
        field: "value",
        headerName: "Navn",
        editable: true,
        valueParser: (params) => (isNumberDropdown ? parseDropdownNumber(params.newValue) ?? params.newValue : params.newValue),
        cellRenderer: ({ value }: CustomCellRendererProps) => {
          if (!isNumberDropdown) {
            return <>{String(value ?? "")}</>;
          }

          const numberValue = parseDropdownNumber(value);
          if (numberValue == null) {
            return <>{String(value ?? "")}</>;
          }

          return <NumberFormatter value={numberValue} thousandSeparator=" " decimalSeparator="," />;
        },
      },
      {
        field: "description",
        headerName: "Beskrivelse",
        editable: true,
        flex: 1,
      },
      {
        field: "color",
        headerName: "Farge",
        editable: true,
        width: 100,
        cellRenderer: ({ value }: CustomCellRendererProps) => {
          return (
            <div
              className="w-100"
              style={{
                minWidth: "60px",
                height: "20px",
                borderRadius: "5px",
                backgroundColor: value,
                outline: value ? "" : "1px solid #e1e1e1",
              }}
            />
          );
        },
        cellEditor: ColorMenuEditor,
        cellEditorPopup: true,
      },
    ],
    [issueBoard?.otherAttributesConfig, isNumberDropdown],
  );

  const newItemTemplateObject: { value: string; description: string } = {
    value: "",
    description: "",
  };

  const onCopyValuesFromOtherAttribute = (propKey: ICapitalLetter) => {
    const otherAttribute = issueBoard?.otherAttributesConfig[propKey];
    if (otherAttribute) {
      updateAttribute("dropdownOptionType", otherAttribute.dropdownOptionType ?? "STRING");
      updateAttribute("tableOptions", otherAttribute.tableOptions);
    }
  };

  const vetRef = useRef<ViewEditTableRef<{ value: string | number; description: string; color?: string }>>(null);

  useImperativeHandle(ref, () => ({
    getData: () => vetRef.current?.getData(),
  }));

  return (
    <div className="d-flex flex-column border p-3 mt-2">
      <h5>Alternativ</h5>
      {otherAttirbutesConfigItemPropsWithDropdown?.length > 0 && (
        <div style={{ width: "200px" }}>
          <McDropdown
            currentValue={null}
            onChange={(_, item) => onCopyValuesFromOtherAttribute(item)}
            defaultTitle="Kopier verdier fra?"
            options={otherAttirbutesConfigItemPropsWithDropdown}
          />
          <VSpace />
        </div>
      )}
      <ViewEditTable<{ value: string | number; description: string; color?: string }>
        columns={colDefs}
        data={
          (editedOtherAttributesConfigItemProps.tableOptions as {
            value: string | number;
            description: string;
            color?: string;
          }[]) || []
        }
        // data is featched manually via getData
        onSave={() => {}}
        newItemTemplateObject={newItemTemplateObject}
        canEdit={true}
        dropdownContainer={"dropdownContainer"}
        alwaysInEditMode={true}
        validators={[VETEmptyAndNoDuplicatesValidator<{ value: string; description: string }>("value")]}
        ref={vetRef}
        useIndexForDragOrder={true}
      />
      <hr />
      <div className="d-flex">
        <h6>Verditype for alternativer</h6>
        <i
          className="fa fa-info-circle fa-fw text-info"
          title="Velg om alle alternativene i denne listen skal lagres som tekst eller tall"
        ></i>
      </div>
      <VSpace />
      <div>
        <ButtonGroup>
          <Button
            disabled={false}
            onClick={() => updateAttribute("dropdownOptionType", "STRING")}
            active={(editedOtherAttributesConfigItemProps.dropdownOptionType ?? "STRING") === "STRING"}
            style={addOrEdit === "edit" ? { pointerEvents: "none", opacity: 1 } : undefined}
          >
            Tekst
          </Button>
          <Button
            disabled={false}
            onClick={() => updateAttribute("dropdownOptionType", "NUMBER")}
            active={editedOtherAttributesConfigItemProps.dropdownOptionType === "NUMBER"}
            style={addOrEdit === "edit" ? { pointerEvents: "none", opacity: 1 } : undefined}
          >
            Tall
          </Button>
        </ButtonGroup>
      </div>
      {addOrEdit === "edit" && (
        <>
          <VSpace />
          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
            Verditypen vises for eksisterende dropdowns, men kan ikke endres etter at attributten er opprettet.
          </div>
        </>
      )}
      <VSpace />
      <div className="d-flex">
        <h6>Et eller flera val?</h6>
        <i
          className="fa fa-info-circle fa-fw text-info"
          title="Angi om du vil kunne velge ett eller flere valg fra listen"
        ></i>
      </div>
      <VSpace />
      <div>
        <ButtonGroup>
          <Button
            disabled={addOrEdit === "edit"}
            onClick={() => updateAttribute("allowMultipleSelectionsInDropdown", false)}
            active={editedOtherAttributesConfigItemProps.allowMultipleSelectionsInDropdown === false}
          >
            Ett valg
          </Button>
          <Button
            disabled={addOrEdit === "edit"}
            onClick={() => updateAttribute("allowMultipleSelectionsInDropdown", true)}
            active={editedOtherAttributesConfigItemProps.allowMultipleSelectionsInDropdown}
          >
            Fler valg
          </Button>
        </ButtonGroup>
      </div>
    </div>
  );
};

const TableOptions = forwardRef<TableOptionsRef, TableOptionsProps>(TableOptionsRefComp);

const parseDropdownNumber = (value: unknown): number | null => {
  const cleaned = String(value ?? "")
    .trim()
    .replace(/[\s\u00A0\u202F]/g, "")
    .replace(",", ".");

  if (cleaned === "") {
    return null;
  }

  const parsed = Number(cleaned);
  return Number.isNaN(parsed) || Number.isFinite(parsed) === false ? null : parsed;
};

const normalizeDropdownOptionRows = ({
  rows,
  dropdownOptionType,
}: {
  rows: { value: string | number; description: string; color?: string }[];
  dropdownOptionType: IOtherAttributeConfigItemProps["dropdownOptionType"];
}) => {
  const validationErrors: string[] = [];
  const normalizedRows: { value: string | number; description: string; color?: string }[] = [];
  const seenValues = new Set<string>();

  rows.forEach((row, index) => {
    const rowNr = index + 1;

    if ((dropdownOptionType ?? "STRING") === "NUMBER") {
      const parsed = parseDropdownNumber(row.value);

      if (parsed == null) {
        validationErrors.push(`Rad ${rowNr}: Verdien må være et gyldig tall.`);
        return;
      }

      const dedupeKey = String(parsed);
      if (seenValues.has(dedupeKey)) {
        validationErrors.push(`Rad ${rowNr}: Verdien må være unik.`);
        return;
      }

      seenValues.add(dedupeKey);
      normalizedRows.push({ ...row, value: parsed });
      return;
    }

    const normalizedValue = String(row.value ?? "");
    if (seenValues.has(normalizedValue)) {
      validationErrors.push(`Rad ${rowNr}: Verdien må være unik.`);
      return;
    }

    seenValues.add(normalizedValue);
    normalizedRows.push({ ...row, value: normalizedValue });
  });

  return { normalizedRows, validationErrors };
};
